import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewEncapsulation,
    ElementRef,
} from '@angular/core';
import {
    FiAccessRightHelperService,
    IAccessRightsCoreFiltersErrors,
} from '@fi-shared/fi-shared-services/fi-access-right-helper.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { IAccessRightCoreFilter } from '@fi-shared/models';

@Component({
    selector: 'fi-manual-selection-section',
    templateUrl: './fi-manual-selection-section.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-manual-selection-section' },
    standalone: false,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FiManualSelectionSectionComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: FiManualSelectionSectionComponent,
            multi: true,
        },
    ],
})
export class FiManualSelectionSectionComponent
    extends ControlValueAccessorValidatorBase<IAccessRightCoreFilter>
    implements OnInit, OnChanges
{
    @Input({ required: true }) availableAccessRightCoreFilter: IAccessRightCoreFilter;
    @Input() isShared: boolean = false;
    @Input() isNewQuickSelectionDisabled: boolean = false;
    @Input() isBusinessAreasSelectionVisible: boolean;
    @Input() hasAreaRights: boolean;
    @Input() headingText: string;
    @Input() descriptionText: string;
    @Input() showErrorTooltip$: Observable<boolean> = new Observable<boolean>();
    @Input() scrollableElementRefs: ElementRef<HTMLElement>[];
    @Input() showRolesCheckboxes: boolean = false;
    @Input() isRolesRequired: boolean = false;
    @Output() onSaveAsNewQuickSelectionChange = new EventEmitter<boolean>();
    @Output() validationErrorsChange = new EventEmitter<IAccessRightsCoreFiltersErrors>();

    protected isSaveAsNewQuickSelection: boolean = false;
    protected isBusinessAreasRequired: boolean = false;
    protected isAreasRequired: boolean = false;
    private fiAccessRightHelperService: FiAccessRightHelperService = inject(FiAccessRightHelperService);

    public ngOnInit(): void {
        this.checkSelectSingleBusinessArea();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['isShared'] || changes['ngModel']) {
            this.updateRequiredFields();
        }
    }

    private isRequired(
        fieldIds: readonly number[] | null | undefined,
        additionalIds: readonly number[] | null | undefined,
    ): boolean {
        if (!this.isShared) {
            return false;
        }
        const selectedBusinessUnitCount = this.ngModel?.BusinessUnitIds?.length ?? 0;
        const currentFieldCount = fieldIds?.length ?? 0;
        const additionalFieldCount = additionalIds?.length ?? 0;
        return selectedBusinessUnitCount > 0 ? additionalFieldCount > 0 : currentFieldCount === 0;
    }

    private computeIsBusinessAreasRequired(): boolean {
        return this.isRequired(this.ngModel.BusinessAreaIds, this.ngModel.AreaIds);
    }

    private computeIsAreasRequired(): boolean {
        return this.isRequired(this.ngModel.AreaIds, this.ngModel.BusinessAreaIds);
    }

    private updateRequiredFields(): void {
        if (!this.ngModel) {
            this.isBusinessAreasRequired = false;
            this.isAreasRequired = false;
            return;
        }
        this.isBusinessAreasRequired = this.computeIsBusinessAreasRequired();
        this.isAreasRequired = this.computeIsAreasRequired();
    }

    protected get accessRightCoreFilter(): IAccessRightCoreFilter {
        return this.ngModel;
    }

    protected isSelectionInfoPanelVisible(): boolean {
        return this.isBusinessAreasSelectionVisible && !this.isBusinessAreasSelectionDisabled() && this.hasAreaRights;
    }

    protected isBusinessAreasSelectionDisabled(): boolean {
        return this.availableAccessRightCoreFilter.BusinessAreas.length === 1;
    }

    protected isBusinessAreaTooltipShown$ = new BehaviorSubject<boolean>(false);
    private updateIsBusinessAreaTooltipShown(validationErrors: IAccessRightsCoreFiltersErrors): void {
        this.isBusinessAreaTooltipShown$.next(
            !validationErrors.area_or_cluster_required && validationErrors.business_area_required,
        );
    }
    protected isAreaTooltipShown$ = new BehaviorSubject<boolean>(false);
    private updateIsAreaTooltipShown(validationErrors: IAccessRightsCoreFiltersErrors): void {
        this.isAreaTooltipShown$.next(
            !validationErrors.business_area_required && validationErrors.area_or_cluster_required,
        );
    }

    protected isRolesTooltipShown$ = new BehaviorSubject<boolean>(false);
    private updateIsRolesTooltipShown(validationErrors: IAccessRightsCoreFiltersErrors): void {
        this.isRolesTooltipShown$.next(validationErrors['roles-not-selected']);
    }

    protected onSelectedAreasChanged(): void {
        this.checkSelectSingleBusinessArea();
        this.updateTooltipVisibility();
    }

    protected onSelectedRolesChanged(): void {
        this.checkSelectSingleBusinessArea();
        this.updateTooltipVisibility();
    }

    private checkSelectSingleBusinessArea(): void {
        this.fiAccessRightHelperService.checkSelectSingleBusinessArea(
            this.availableAccessRightCoreFilter,
            this.accessRightCoreFilter,
            () => this.cdr.markForCheck(),
        );
    }

    public updateTooltipVisibility(): void {
        this.updateRequiredFields();
        const validationErrors: IAccessRightsCoreFiltersErrors = this.getValidationErrors();
        this.updateIsBusinessAreaTooltipShown(validationErrors);
        this.updateIsAreaTooltipShown(validationErrors);
        this.updateIsRolesTooltipShown(validationErrors);
        this.validationErrorsChange.emit(validationErrors);
        this.doPropagateChanges();
    }

    private getValidationErrors(): IAccessRightsCoreFiltersErrors {
        return this.fiAccessRightHelperService.validateAccessRightsCoreFilters(this.accessRightCoreFilter, {
            isShared: this.isShared,
            skipRolesCheck: !this.showRolesCheckboxes && !this.isRolesRequired,
        });
    }

    protected setSaveAsNewQuickSelection(): void {
        this.onSaveAsNewQuickSelectionChange.emit(this.isSaveAsNewQuickSelection);
    }
}
